// src/components/steps/AccountSetup.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import CustomSelect from "./AccountSetup/CustomSelect";
import { useProfile } from "../../contexts/ProfileContext";
import SkipToDashboardButton from "../../components/dashboard/common/SkipToDashboardButton";
import ResumeUploaderAI from "../common/ResumeUploaderAI"; // Import the enhanced component
import { uploadAndParseResume } from "../../services/api";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// --- Icons (kept local to this component) ---
const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-4 h-4 ml-2"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8.25 4.5l7.5 7.5-7.5 7.5"
    />
  </svg>
);

const UserPlaceholderIcon = () => (
  <svg
    className="w-full h-full text-gray-300"
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      clipRule="evenodd"
      fillRule="evenodd"
      d="M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
    />
  </svg>
);

// --- UPDATED: Reusable Saving Animation Component ---
const SavingAnimation = ({ isSaving, message, subMessage }) => {
  if (!isSaving) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex flex-col items-center justify-center z-40 transition-opacity duration-300"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="text-center p-8 rounded-lg">
        <svg
          className="animate-spin h-10 w-10 text-emerald-600 mb-4 mx-auto"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p className="text-lg font-semibold text-gray-800">
          {message || "Saving..."}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {subMessage || "Please wait a moment."}
        </p>
      </div>
    </div>
  );
};

function AccountSetup({
  onNext,
  onBack,
  onSkipToDashboard,
  isSubmitting,
  isParsingAI,
  onAIAssistedFill,
}) {
  const {
    formData,
    updateFormSection,
    error: contextError,
    setError: setContextError,
    processResumeUpload,
  } = useProfile();

  const [localError, setLocalError] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isProcessingResume, setIsProcessingResume] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [resumeSuccess, setResumeSuccess] = useState(false);

  const photoInputRef = useRef(null);

  const isLoading = isSubmitting || isProcessingResume || isNavigating;
  const isOverlayVisible = isSubmitting || isNavigating;

  useEffect(() => {
    const localFile = formData.account?.photo;
    const remoteRelativeUrl = formData.account?.photoUrl;
    let newTargetUrl = null;

    if (localFile instanceof File) {
      newTargetUrl = URL.createObjectURL(localFile);
    } else if (remoteRelativeUrl) {
      newTargetUrl = `${API_BASE_URL}${
        remoteRelativeUrl.startsWith("/") ? "" : "/"
      }${remoteRelativeUrl}`;
    }

    // This logic needs to be safe against revoking the same URL multiple times.
    // The previous implementation had a potential issue.
    const oldUrl = photoPreviewUrl;
    if (newTargetUrl !== oldUrl) {
      if (oldUrl && oldUrl.startsWith("blob:")) {
        URL.revokeObjectURL(oldUrl);
      }
      setPhotoPreviewUrl(newTargetUrl);
    }

    return () => {
      if (newTargetUrl && newTargetUrl.startsWith("blob:")) {
        URL.revokeObjectURL(newTargetUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.account?.photo, formData.account?.photoUrl]);

  const accountTypeOptions = [
    { value: "Job Seeker", label: "Job Seeker" },
    { value: "Employer", label: "Employer" },
    { value: "Both", label: "Both" },
  ];
  const timeZoneOptions = [
    { value: "Pacific", label: "Pacific (PST/PDT, UTC-8/UTC-7)" },
    { value: "Mountain", label: "Mountain (MST/MDT, UTC-7/UTC-6)" },
    { value: "Central", label: "Central (CST/CDT, UTC-6/UTC-5)" },
    { value: "Eastern", label: "Eastern (EST/EDT, UTC-5/UTC-4)" },
    { value: "Atlantic", label: "Atlantic (AST/ADT, UTC-4/UTC-3)" },
    {
      value: "Newfoundland",
      label: "Newfoundland (NST/NDT, UTC-3:30/UTC-2:30)",
    },
  ];

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFormSection({ section: "account", data: { [name]: value } });
      if (localError) setLocalError(null);
      if (contextError) setContextError(null);
    },
    [updateFormSection, localError, contextError, setContextError]
  );

  const handlePhotoFileChange = useCallback(
    (e) => {
      if (localError) setLocalError(null);
      if (contextError) setContextError(null);
      const file = e.target.files && e.target.files[0];
      e.target.value = null;
      if (file) {
        const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
        if (!allowedTypes.includes(file.type)) {
          setLocalError("Invalid file type. Please use JPG, PNG, or WEBP.");
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setLocalError("File is too large. Maximum size is 5MB.");
          return;
        }
        updateFormSection({
          section: "account",
          data: { photo: file, photoUrl: null },
        });
      }
    },
    [updateFormSection, localError, contextError, setContextError]
  );

  const handleSubmitAndNavigate = (e) => {
    e.preventDefault();
    setLocalError(null);
    if (contextError) setContextError(null);
    const { firstName, lastName, email } = formData.account;
    if (!firstName || !lastName || !email) {
      setLocalError("First name, last name, and email are required.");
      return;
    }

    setIsNavigating(true);
    setTimeout(() => {
      if (onNext) {
        onNext(formData.account);
      }
    }, 1000);
  };

  // NEW: Handle resume upload with the new processResumeUpload function
  const handleResumeUpload = async (file) => {
    if (isLoading || !file) return;

    console.log("=== Starting resume upload and parse ===");
    setIsProcessingResume(true);
    setResumeError(null);
    setResumeSuccess(false);

    try {
      // Step 1: Upload and parse the resume using AI
      console.log("Uploading resume file:", file.name);
      const parsedData = await uploadAndParseResume(file);

      console.log("=== Parsed resume data received ===");
      console.log("Full parsed data:", parsedData);

      // Debug: Check education descriptions
      if (parsedData.education) {
        console.log("=== Education data from AI ===");
        parsedData.education.forEach((edu, idx) => {
          console.log(`Education ${idx}:`, edu);
          console.log(`- Institution: ${edu.institutionName}`);
          console.log(`- Degree: ${edu.degree}`);
          console.log(`- Description: ${edu.description || "NO DESCRIPTION"}`);
        });
      }

      // Step 2: Process and save using ProfileContext
      console.log("Processing parsed data and saving to database...");
      const saveResults = await processResumeUpload(parsedData);

      console.log("Save results:", saveResults);

      // Step 3: Update local form with the parsed data
      if (parsedData.skills_and_profile) {
        const nameParts = (parsedData.skills_and_profile.full_name || "").split(
          " "
        );
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Update form with parsed data
        updateFormSection({
          section: "account",
          data: {
            firstName: firstName || formData.account?.firstName,
            lastName: lastName || formData.account?.lastName,
            phoneNumber:
              parsedData.skills_and_profile.phone_number ||
              formData.account?.phoneNumber,
            location:
              parsedData.skills_and_profile.location ||
              formData.account?.location,
            headline:
              parsedData.skills_and_profile.headline ||
              formData.account?.headline,
          },
        });
      }

      setResumeSuccess(true);

      // Log success message for debugging
      const message = `Successfully processed resume:
        - Profile updated
        - ${saveResults.education.length} education records saved
        - ${saveResults.experience.length} experience records saved
        - ${saveResults.skills.length} skills added
        - ${saveResults.licenses?.length || 0} licenses added`;

      console.log(message);

      // Clear success state after a few seconds
      setTimeout(() => {
        setResumeSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Resume upload error:", error);
      setResumeError(
        error.message || "Failed to process resume. Please try again."
      );
      // Clear error after 5 seconds
      setTimeout(() => {
        setResumeError(null);
      }, 5000);
    } finally {
      setIsProcessingResume(false);
    }
  };

  const hasPhotoToShow =
    formData.account?.photo instanceof File || formData.account?.photoUrl;
  const buttonText = hasPhotoToShow ? "Change photo" : "Upload photo";

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-8 font-sans">
      <SavingAnimation
        isSaving={isOverlayVisible}
        message={
          isNavigating ? "Proceeding to next step..." : "Saving your profile..."
        }
        subMessage={isNavigating ? "Saving..." : undefined}
      />

      <div className="form-header mb-6 text-center">
        <h1 className="text-3xl font-semibold text-gray-900 mb-2">
          Set Up Your Profile
        </h1>
        <p className="text-sm text-gray-500 px-4">
          Create your profile and begin your job matching journey
        </p>
      </div>

      <ResumeUploaderAI
        onUpload={handleResumeUpload}
        isUploading={isProcessingResume}
      />

      {/* Show success or error messages*/}
      {resumeSuccess && (
        <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
          <div className="flex">
            <svg
              className="h-5 w-5 text-green-400 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-green-800">Success!</h3>
              <p className="mt-1 text-sm text-green-700">
                Your resume has been processed and your profile has been
                updated.
              </p>
            </div>
          </div>
        </div>
      )}

      {resumeError && (
        <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{resumeError}</p>
            </div>
          </div>
        </div>
      )}

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-base font-semibold leading-6 text-gray-900">
            Or fill it out manually
          </span>
        </div>
      </div>

      {localError && (
        <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{localError}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmitAndNavigate} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              First Name *
            </label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              value={formData.account?.firstName || ""}
              onChange={handleChange}
              className="block w-full rounded-md border border-gray-300 shadow-sm p-3 focus:border-indigo-500 focus:ring-indigo-500"
              required
              autoComplete="given-name"
              disabled={isLoading}
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last Name *
            </label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              value={formData.account?.lastName || ""}
              onChange={handleChange}
              className="block w-full rounded-md border border-gray-300 shadow-sm p-3 focus:border-indigo-500 focus:ring-indigo-500"
              required
              autoComplete="family-name"
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.account?.email || ""}
              readOnly={true}
              className="block w-full rounded-md border border-gray-300 shadow-sm p-3 bg-gray-100 cursor-not-allowed"
            />
          </div>
          <div>
            <label
              htmlFor="phoneNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Phone Number *
            </label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={formData.account?.phoneNumber || ""}
              onChange={handleChange}
              className="block w-full rounded-md border border-gray-300 shadow-sm p-3"
              placeholder="555-123-4567"
              required
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
          <CustomSelect
            label="Account Type"
            name="accountType"
            options={accountTypeOptions}
            value={formData.account?.accountType || "Job Seeker"}
            onChange={handleChange}
            disabled={isLoading}
          />
          <CustomSelect
            label="Time Zone"
            name="timeZone"
            options={timeZoneOptions}
            value={formData.account?.timeZone || "Eastern"}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>
        <div className="pt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profile Photo
          </label>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-20 h-20 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
              {photoPreviewUrl ? (
                <img
                  src={photoPreviewUrl}
                  alt="Profile Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserPlaceholderIcon />
              )}
            </div>
            <div>
              <input
                id="photo-upload-input"
                name="photoFile"
                type="file"
                className="sr-only"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handlePhotoFileChange}
                ref={photoInputRef}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={isLoading}
                className="cursor-pointer rounded-md border border-gray-300 bg-white py-2 px-3 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {buttonText}
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center pt-8 gap-4 border-t border-gray-200 mt-6">
          <div className="flex flex-col sm:flex-row gap-x-4 gap-y-4 w-full sm:w-auto order-last sm:order-first">
            {onSkipToDashboard && (
              <SkipToDashboardButton
                onSkip={onSkipToDashboard}
                disabled={isLoading}
              />
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full sm:w-auto order-first sm:order-last inline-flex items-center justify-center rounded-md border border-transparent bg-emerald-700 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:opacity-50"
          >
            {isSubmitting ? "Saving..." : "Save & Next"}
            {!isOverlayVisible && <ArrowRightIcon />}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AccountSetup;
